import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { NgxLoggerService } from '@go-modules/services/logger/logger.service';

type UserpilotPropertyValue = string | number | boolean | null | undefined;

export interface UserpilotIdentifyProperties {
	[key: string]: UserpilotPropertyValue;
}

interface UserpilotSdk {
	identify(userId: string, properties?: Record<string, UserpilotPropertyValue>): void;
	reload(options?: { url?: string }): void;
	end?(): void;
	clean?(): void;
	destroy?(): void;
}

const USERPILOT_SDK_ID = 'userpilot-sdk';

@Injectable({
	providedIn: 'root'
})
export class NgxUserpilotService {
	private identifiedUserId: string | null = null;
	private loadSdkPromise?: Promise<boolean>;

	constructor (
		private featureFlagService: NgxFeatureFlagService,
		private logger: NgxLoggerService,
		@Inject(DOCUMENT) private document: Document,
		@Inject('Window') private window: Window
	) {}

	public async initialize (): Promise<boolean> {
		if (!this.isEnabled()) {
			return false;
		}

		if (this.getSdk() != null) {
			return true;
		}

		if (this.loadSdkPromise == null) {
			this.loadSdkPromise = this.loadSdk();
		}

		return this.loadSdkPromise;
	}

	public async identify (userId: string | number, properties: UserpilotIdentifyProperties = {}): Promise<boolean> {
		if (!this.isEnabled() || userId == null) {
			return false;
		}

		const normalizedUserId = String(userId);
		if (this.identifiedUserId === normalizedUserId) {
			return true;
		}

		const initialized = await this.initialize();
		if (!initialized) {
			return false;
		}

		try {
			this.getSdk()?.identify(normalizedUserId, this.cleanProperties(properties));
			this.identifiedUserId = normalizedUserId;
			return true;
		} catch (error) {
			this.logger.error(error);
			return false;
		}
	}

	public async reload (): Promise<void> {
		if (!this.isEnabled() || this.identifiedUserId == null) {
			return;
		}

		const initialized = await this.initialize();
		if (!initialized) {
			return;
		}

		try {
			this.getSdk()?.reload();
		} catch (error) {
			this.logger.error(error);
		}
	}

	public reset (): void {
		this.identifiedUserId = null;

		const sdk = this.getSdk();
		if (sdk == null) {
			return;
		}

		try {
			if (typeof sdk.destroy === 'function') {
				sdk.destroy();
				return;
			}

			sdk.end?.();
			sdk.clean?.();
		} catch (error) {
			this.logger.error(error);
		}
	}

	private async loadSdk (): Promise<boolean> {
		try {
			const token = clientSettings.userpilot?.token;
			this.window.userpilotSettings = {
				...(this.window.userpilotSettings ?? {}),
				token
			};

			await new Promise<void>((resolve, reject) => {
				const existingScript = this.document.getElementById(USERPILOT_SDK_ID) as HTMLScriptElement | null;
				if (existingScript != null) {
					if (this.getSdk() != null) {
						resolve();
						return;
					}

					existingScript.addEventListener('load', () => resolve(), { once: true });
					existingScript.addEventListener('error', () => reject(new Error('Userpilot SDK failed to load')), { once: true });
					return;
				}

				const script = this.document.createElement('script');
				script.id = USERPILOT_SDK_ID;
				script.src = 'https://js.userpilot.io/sdk/latest.js';
				script.async = true;
				script.addEventListener('load', () => resolve(), { once: true });
				script.addEventListener('error', () => reject(new Error('Userpilot SDK failed to load')), { once: true });
				this.document.head.appendChild(script);
			});

			return this.getSdk() != null;
		} catch (error) {
			this.loadSdkPromise = undefined;
			this.logger.error(error);
			return false;
		}
	}

	private isEnabled (): boolean {
		return this.featureFlagService.isAvailable('USERPILOT') &&
			clientSettings.userpilot?.token != null &&
			clientSettings.userpilot.token !== '';
	}

	private getSdk (): UserpilotSdk | null {
		return this.window.userpilot as UserpilotSdk ?? null;
	}

	private cleanProperties (properties: UserpilotIdentifyProperties): Record<string, UserpilotPropertyValue> {
		return Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined));
	}
}
